import React, { forwardRef, useCallback, useId, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import moment from 'moment';

import { AvatarType, getAvatarImage, Size } from '@rover/components/src/Avatar';
import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import StackedAvatar from '@rover/components/src/StackedAvatar';
import CalendarCancellation from '@rover/icons/svg/calendar-cancellation.svg';
import { Box, Flex, Paragraph, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock';
import StarRating from '@rover/kibble/patterns/StarRating/StarRating';
import useTheme from '@rover/kibble/themes/useTheme';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { TrainerProfileReviewClicked } from '@rover/react-lib/src/pages/training/events/analyticsEvents';
import { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';

import '@rover/utilities/allMomentLocales';

import {
  getIcon,
  isAnyRating,
  isAnyTestimonial,
  isAutomaticRecord,
  isExternalReview,
} from './utils';

type TVariant = 'training' | 'profile-revamp';

export type Props = {
  review: ReviewDispatch;
  onReadMore?: () => void;
  onLearnMore?: () => void;
  full?: boolean;
  variant?: TVariant;
  showRating?: boolean;
  compact?: boolean;
};

const MAX_NUM_LINES = 4;
const LINE_HEIGHT = 24;

const Review = forwardRef<HTMLElement, Props>(
  (
    {
      review,
      onReadMore,
      onLearnMore,
      full = false,
      variant,
      showRating = false,
      compact = false,
    }: Props,
    ref
  ) => {
    const { t } = useLingui();
    const theme = useTheme();
    const { locale, DF } = useI18nContext();
    moment.locale(locale);
    const [isTruncated, setIsTruncated] = useState<boolean>(false);

    const handleReadMore = (): void => {
      onReadMore && onReadMore();
      if (variant === 'training') {
        const analyticsEvent = new TrainerProfileReviewClicked();
        emitAnalyticsEvent(analyticsEvent);
        Logger.info(analyticsEvent.eventName);
      } else {
        emitMetric('sitter_profile_review_clicked');
        Logger.info('sitter_profile_review_clicked');
      }
    };

    const renderCalendarIcon = (dimension: number): JSX.Element => {
      return (
        <Flex
          height={dimension}
          width={dimension}
          borderRadius="40px"
          justifyContent="center"
          alignItems="center"
          sx={{ backgroundColor: theme.background.tertiary }}
        >
          <CalendarCancellation height={16} width={16} />
        </Flex>
      );
    };

    const service = (isAnyRating(review) || isAutomaticRecord(review)) && review.service;

    const petParentName =
      (isAnyTestimonial(review) || isAnyRating(review)) && review.poster.shortName;

    const reviewText = review.description;
    const petAvatars =
      isAnyRating(review) &&
      review.pets?.map((pet) =>
        getAvatarImage(pet.defaultImage.bigThumb, pet.petType as AvatarType)
      );

    // Pin both the timezone ('.utc()') and this instance's locale to the request's
    // locale so the rendered date is identical on the server and the client, instead
    // of depending on moment's mutable global locale (which other components freely
    // reassign) — the divergence that caused the SSR hydration mismatch. Using the
    // request locale rather than a hardcoded default keeps months localized.
    const reviewDate = moment(review.added).utc().locale(locale).format(DF.DATE_MED);
    const subtitle = `${service ? `${service.name} • ` : ''}  ${reviewDate}`;

    const paragraphStyle = {
      display: '-webkit-box',
      overflow: 'hidden',
      'white-space': 'pre-line',
      '-webkit-line-clamp': `${MAX_NUM_LINES}`,
      '-webkit-box-orient': 'vertical',
    };

    const reviewDescriptionId = useId();
    const measurementRef = useCallback((node) => {
      if (node) {
        const { height } = node.getBoundingClientRect();
        if (Math.round(height / LINE_HEIGHT) > MAX_NUM_LINES) {
          setIsTruncated(true);
        }
      }
    }, []);

    const reviewLabel = petParentName ? t`Review by ${petParentName}` : t`Review`;

    return (
      <Flex
        flexDirection="column"
        width="100%"
        webProps={{
          // Focusable group so JS-driven focus lands on a named element
          // (NVDA reads the label instead of announcing "blank").
          tabIndex: -1,
          role: 'group',
          'aria-label': reviewLabel,
        }}
      >
        <Flex
          width="100%"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          pb={isExternalReview(review) ? '0x' : '4x'}
        >
          <Box id={reviewDescriptionId} flexDirection="column" ref={ref}>
            {petParentName && <Text fontWeight="semibold">{petParentName}</Text>}
            {showRating && isAnyRating(review) && (
              <Box pt="1x">
                <StarRating rating={review.overall} variant="expand" textSize="100" />
              </Box>
            )}
            {(isAnyRating(review) || isAutomaticRecord(review)) && service && (
              <Box pt="1x">
                <IconBlock
                  iconSize="16px"
                  size="100"
                  fontWeight="regular"
                  title={subtitle}
                  icon={getIcon(service.icon)}
                  iconMargin="2x"
                />
              </Box>
            )}
            {isAnyTestimonial(review) && (
              <Box pt="1x">
                <Text size="100" fontWeight="regular">
                  {subtitle}
                </Text>
              </Box>
            )}
          </Box>
          {!compact && isAnyRating(review) && petAvatars && (
            <StackedAvatar imageUrls={petAvatars} size={Size.Small} />
          )}
          {isAutomaticRecord(review) && variant !== 'profile-revamp' && renderCalendarIcon(48)}
        </Flex>
        <Box ref={measurementRef}>
          <Paragraph mb="0x" sx={!full && isTruncated ? paragraphStyle : {}}>
            {reviewText}
          </Paragraph>
        </Box>
        {compact
          ? isAnyRating(review) &&
            petAvatars && <StackedAvatar imageUrls={petAvatars} size={Size.ExtraSmall} />
          : isTruncated &&
            !full && (
              <Box pt="4x">
                <FlatButton aria-describedby={reviewDescriptionId} onClick={handleReadMore}>
                  {t`Read more`}
                </FlatButton>
              </Box>
            )}
        {isAutomaticRecord(review) && (
          <>
            <Box pt="4x">
              <FlatButton aria-describedby={reviewDescriptionId} onClick={onLearnMore}>
                {t`Learn more`}
              </FlatButton>
            </Box>
            {variant === 'profile-revamp' && <Box pt="8x"> {renderCalendarIcon(32)} </Box>}
          </>
        )}
      </Flex>
    );
  }
);

export default Review;
